/**
 * MOTOR DE WI-FI — presupuesto de enlace, cobertura estimada y número de puntos de acceso.
 * =============================================================================
 *
 * Cuarto rubro del levantamiento guiado, y el que sostiene la columna de conectividad del
 * negocio: `Marca/Plan_Negocio_Soham_IoT.md` pone a Cisco Meraki como espina dorsal de
 * Soham Home, y todo lo demás —cámaras, automatización, sensores— cuelga de que la red
 * llegue bien a donde se instala.
 *
 * ---------------------------------------------------------------------------
 * LA ADVERTENCIA VA PRIMERO, PORQUE CAMBIA CÓMO SE USA ESTE MÓDULO
 *
 * **Esto NO predice la cobertura. Estima un punto de partida para ir a medirla.**
 *
 * La propagación de radio en una casa real depende de cosas que ningún plano contiene: el
 * refrigerador de acero, el espejo del baño, la maceta grande, el vecino con su propio
 * router en el mismo canal. La documentación de Meraki es explícita en que el diseño se
 * valida con un *site survey*, no con un cálculo.
 *
 * Lo que sí hace este módulo, y es mucho:
 *   · Dice **cuántos puntos de acceso pedir**, que es la decisión de compra.
 *   · Dice **dónde ir a medir**, que es lo que convierte una visita en un levantamiento.
 *   · Traduce «no me llega el WiFi al fondo» a un número que se puede discutir.
 *
 * Por eso todos los resultados salen marcados `estimado: true`. No es humildad decorativa:
 * es la diferencia entre vender un diseño y vender una hipótesis con plan de verificación.
 * ============================================================================= */

import { area, dist, dentro, caja, cobertura } from './geometria.js'
import { resolver, renglon } from './catalogo-tecnico.js'

export { area, dist, dentro, cobertura }

/* ============================================================================
   1. UMBRALES — qué se considera «buena señal»
   ========================================================================== */

/**
 * Calidad de señal por nivel recibido, en dBm.
 *
 * `FUENTE:` documentación de Cisco Meraki, *«Conducting Site Surveys with MR Access
 * Points»* — que fija el **borde de celda en -67 dBm** para una red de grado voz — y
 * *«Signal-to-Noise Ratio (SNR) and Wireless Signal Strength»*, que fija **SNR ≥ 20 dB
 * para datos y ≥ 25 dB para voz**. Las dos leídas de `documentation.meraki.com`.
 *
 * Se eligió el criterio de Meraki y no uno genérico porque es la marca que el negocio
 * distribuye: diseñar contra el umbral del fabricante que se va a instalar evita la
 * discusión de por qué el dashboard marca en amarillo algo que el diseño decía bueno.
 *
 * **Ojo con el detalle que la propia documentación subraya:** lo que manda no es el dBm
 * absoluto sino el **SNR**. Meraki lo dice con un ejemplo: *«-65 dBm puede considerarse
 * bueno donde el piso de ruido es -90 dBm (SNR 25) pero no donde es -80 dBm (SNR 15)»*.
 * Por eso este módulo pide el piso de ruido medido y no lo supone.
 */
export const SENAL = [
  { id: 'excelente', dbm: -60, nombre: 'Excelente', que: 'Todo funciona, incluido video en varias pantallas.' },
  { id: 'voz', dbm: -67, nombre: 'Grado voz', que: 'Borde de celda recomendado. Aguanta llamadas y video.' },
  { id: 'datos', dbm: -72, nombre: 'Grado datos', que: 'Navegar y streaming sí; llamadas se cortan al moverse.' },
  { id: 'pobre', dbm: -80, nombre: 'Pobre', que: 'Conecta y se cae. Peor que no tener señal, porque el cliente lo reporta como falla.' }
]

/** `FUENTE:` Meraki. SNR mínimo utilizable, en dB. */
export const SNR = {
  datos: 20,
  voz: 25,
  fuente: 'Cisco Meraki, «Signal-to-Noise Ratio (SNR) and Wireless Signal Strength»'
}

/** Borde de celda con el que se diseña por defecto: grado voz. */
export const BORDE_CELDA = -67

/* ============================================================================
   2. ATENUACIÓN POR MATERIAL
   ========================================================================== */

/**
 * Pérdida en dB al atravesar un elemento constructivo.
 *
 * ✅ **DEUDA RESUELTA EL 2026-08-15, Y CON UN HALLAZGO NEGATIVO QUE IMPORTA.**
 *
 * La *Wireless RF Reference Guide* de Cisco devolvía **403** al extractor y a `curl` con
 * agente de navegador. Se abrió con el navegador real —la misma URL, sin trucos— y sí
 * carga: el servidor bloquea clientes automatizados, no el contenido.
 *
 * **Y resultó que esa guía NO tiene tabla de atenuación.** Tiene una sola viñeta con tres
 * valores, textual: *«Los objetos típicos son muros de ladrillo (10 dB de atenuación);
 * concreto, que tiene más metal (12 dB); y tablaroca (típicamente 3 dB). La cantidad de
 * atenuación depende del metal, la humedad, el espesor y la conductividad.»*
 *
 * Es un caso de manual de la regla 2 del `CLAUDE.md` raíz: **el resumen de búsqueda había
 * fabricado una tabla** juntando esos tres valores con los de otra página de Cisco, y yo
 * cargué los seis como si vinieran del mismo documento. Tres eran ciertos y tres no tenían
 * la procedencia que decían.
 *
 * Así queda cada renglón:
 *   · `FUENTE:` texto primario — ladrillo 10, concreto 12, tablaroca 3.
 *   · `CONFIRMAR:` los demás. Siguen siendo órdenes de magnitud razonables y siguen sin
 *     tener fuente leída. Se conservan porque el módulo los necesita, marcados para que
 *     nadie los confunda con los tres de arriba.
 *
 * La discrepancia con Meraki —que menciona 24 a 35 dB para concreto o vidrio Low-E— queda
 * explicada por la propia ITU: 12 dB es un muro interior; una **losa** de concreto
 * reforzado son 20 a 36 dB. Ver `LOSA_CONCRETO`. No se contradicen: hablan de cosas
 * distintas.
 */
export const MATERIALES = [
  { id: 'tablaroca', nombre: 'Muro de tablaroca', db: 3, fuente: 'Cisco RF Reference Guide',
    comun: 'Divisiones interiores' },
  { id: 'ladrillo', nombre: 'Muro de ladrillo', db: 10, fuente: 'Cisco RF Reference Guide',
    comun: 'Construcción antigua' },
  { id: 'concreto', nombre: 'Muro de concreto', db: 12, fuente: 'Cisco RF Reference Guide',
    comun: 'Muros de carga y castillos. La guía anota que el concreto pesa más por su metal' },
  /* Los tres de abajo NO están en la guía de Cisco. Se conservan porque el módulo los
     necesita y son órdenes de magnitud razonables, pero sin fuente leída. */
  { id: 'block', nombre: 'Muro de block hueco', db: 4, sinVerificar: true,
    comun: 'Lo más común en vivienda mexicana, y el que más falta hace verificar' },
  { id: 'vidrio', nombre: 'Cancel de vidrio con marco metálico', db: 6, sinVerificar: true,
    comun: 'Ventanales y canceles' },
  { id: 'puerta-metal', nombre: 'Puerta metálica en muro', db: 12, sinVerificar: true,
    comun: 'Cuarto de máquinas, bodega' },
  { id: 'concreto-grueso', nombre: 'Losa de concreto reforzado', db: 20,
    fuente: 'UIT-R P.1238-7, mediciones a 5.2 GHz',
    comun: 'Entrepisos. Sube a 30 con luminarias y a 36 con ductos — ver LOSA_CONCRETO' }
]

/**
 * ⚠️ DISCREPANCIA ENTRE DOS DOCUMENTOS DEL MISMO FABRICANTE, y conviene saberla.
 *
 * La *Wireless RF Reference Guide* de Cisco dice, textual: *«-66 dBm se usa a menudo como
 * valor estándar para hacer site survey de cobertura de cliente. Nota: en su momento se
 * usaba -67. Sin embargo, para dispositivos Cisco IOS más nuevos y teléfonos Wi-Fi más
 * nuevos, -66 dBm es un mejor número.»*
 *
 * La documentación de Meraki sigue diciendo **-67**.
 *
 * Un decibel no cambia un diseño —son menos del 3% de distancia— pero se registra porque
 * es exactamente el tipo de detalle que un cliente técnico saca en una junta. **El módulo
 * usa -67** porque Meraki es la marca que el negocio instala y es su dashboard el que va a
 * pintar el mapa de calor: diseñar contra el umbral de otro documento sería discutir con
 * la herramienta que se entrega.
 */
export const BORDE_CELDA_CISCO = {
  valor: -66,
  fuente: 'Cisco Wireless RF Reference Guide, abril 2024',
  porque: 'Cisco actualizó de -67 a -66 para equipo y teléfonos recientes. Meraki sigue en ' +
          '-67 y es la marca que se instala, así que el módulo diseña con -67.'
}

export const materialPorId = id => MATERIALES.find(m => m.id === id) || MATERIALES[1]

/* ============================================================================
   3. PRESUPUESTO DE ENLACE
   ========================================================================== */

/**
 * ⚠️ CORRECCIÓN DE FONDO, 2026-08-14. La primera versión de este módulo dimensionaba con
 * **pérdida en espacio libre**, y estaba mal de una forma que importa.
 *
 * Espacio libre daba **137 m de alcance a 5 GHz dentro de una casa**. Lo detectó una
 * prueba que esperaba que una casa con más muros pidiera más puntos de acceso y falló:
 * con alcances así de largos, cualquier casa cabía en un solo equipo. El número era
 * absurdo y empujaba en la dirección peligrosa —cotizar de menos y entregar una red que
 * no llega—, que además es la que nadie reclama hasta después de instalada.
 *
 * La razón es física: espacio libre supone que la potencia decae con el cuadrado de la
 * distancia, y **dentro de un edificio decae mucho más rápido** por dispersión, muebles,
 * pisos y reflexiones. Se sustituyó por el modelo de la recomendación **ITU-R P.1238**,
 * que es el estándar internacional para propagación en interiores.
 *
 * `perdidaEspacioLibre` se conserva porque sigue siendo correcta para lo que sí es
 * espacio libre —un enlace exterior con línea de vista, del tipo casa a caseta— y porque
 * es la referencia contra la que se ve cuánto castiga estar adentro.
 *
 *     FSPL = 20·log₁₀(d_km) + 20·log₁₀(f_MHz) + 32.44
 */
export function perdidaEspacioLibre (metros, mhz) {
  if (metros <= 0) return 0
  return 20 * Math.log10(metros / 1000) + 20 * Math.log10(mhz) + 32.44
}

/**
 * MODELO DE INTERIORES — recomendación ITU-R P.1238.
 *
 * `FUENTE:` **texto primario**, Recomendación UIT-R P.1238-7, descargada de `itu.int` y
 * leída con poppler. Ecuación (1) de su sección 3.1:
 *
 *     L_total = 20·log₁₀(f) + N·log₁₀(d) + L_f(n) − 28   dB
 *
 * con `f` en MHz, `d` en metros, `N` el coeficiente de pérdida por distancia y `L_f` la
 * penalización por atravesar `n` pisos.
 *
 * **Lo más importante de leer la fuente, y lo que cambia cómo se usa este módulo:** la
 * propia recomendación dice que los coeficientes *«incluyen una previsión implícita de la
 * transmisión a través de muros y por encima y a través de obstáculos»*. Es decir, **N ya
 * trae los muros interiores adentro**.
 *
 * Consecuencia dura: **no se le suma encima la tabla de `MATERIALES`.** Hacerlo contaría
 * dos veces los mismos muros. La recomendación distingue explícitamente los dos caminos —
 * *«los modelos específicos del sitio tendrían la opción de contabilizar explícitamente la
 * pérdida de cada muro en lugar de incluirla en el modelo de distancia»*— y este archivo
 * respeta esa separación:
 *
 *   · `alcance()` usa ITU, para dimensionar **cuántos equipos** hacen falta.
 *   · `nivelRecibido()` usa espacio libre más muros explícitos, para responder **un enlace
 *     concreto**: «¿le llega desde el pasillo a la cámara de la terraza cruzando esos dos
 *     muros?».
 *
 * Son preguntas distintas y se contestan con modelos distintos. Mezclarlos es el error.
 */
export const ITU = {
  fuente: 'Recomendación UIT-R P.1238-7, ecuación (1) y Tabla 2',
  /**
   * Tabla 2 de la recomendación, coeficiente `N`. Transcrita tal cual.
   *
   * La nota al pie define los dos tipos residenciales, y la distinción es exactamente la
   * que separa una casa mexicana de un departamento mexicano:
   *   · **`casa`**: vivienda de una o dos plantas para una familia. *«La mayoría de los
   *     muros que separan cuartos son de madera.»*
   *   · **`departamento`**: vivienda para varias familias. *«La mayoría de los muros que
   *     separan cuartos son de concreto.»*
   *
   * ⚠️ **Ojo con esto al cotizar en México.** La construcción residencial de aquí es de
   * block y concreto, no de madera. Aunque el cliente viva en una casa unifamiliar, el
   * coeficiente que le corresponde es casi siempre el de **`departamento`**, que es el de
   * muros de concreto. Usar el de `casa` porque el inmueble es una casa subestima la
   * pérdida y cotiza de menos.
   */
  N: {
    '2.4': { casa: 28, departamento: 28, oficina: 30 },
    '5': { casa: 28, departamento: 30, oficina: 31 },
    /* La Tabla 2 no publica valor residencial a 5.8 GHz ni nada a 6 GHz —la banda no
       existía cuando se escribió—. Se usa el de 5.2 GHz, que es el vecino más cercano y
       el más conservador de los publicados. Va marcado. */
    '6': { casa: 28, departamento: 30, oficina: 31, extrapolado: true }
  },
  /**
   * Tabla 3 — penalización por piso atravesado, en dB.
   *
   * ✅ **RESUELTA Y CORREGIDA EL 2026-08-15.** Quedó como deuda porque `pdftotext -layout`
   * entrevera las columnas de esta tabla; **`pdftotext -raw` la devuelve limpia.** Es el
   * modo que hay que usar cuando una tabla de PDF sale revuelta: `-layout` intenta
   * preservar la posición y falla con columnas de varias líneas, `-raw` respeta el orden
   * del flujo del documento.
   *
   * Y al leerla bien apareció un error propio: tenía **5 dB para casa**, que es el valor
   * de **2.4 GHz**. El de 5.2 GHz es **7**. Los estaba mezclando entre bandas.
   *
   * Tabla 3 completa para residencial, transcrita:
   *   · 1.8–2 GHz:  4·n
   *   · 2.4 GHz:    10 por muro de concreto (departamento) · 5 (casa)
   *   · 5.2 GHz:    13 por muro de concreto (departamento) · 7 (casa, mortero de madera)
   */
  Lf: {
    '2.4': { casa: 5, departamento: 10, oficina: 14 },
    '5': { casa: 7, departamento: 13, oficina: 16 },
    /* 6 GHz no existe en la recomendación; se usa el de 5.2, que es el vecino. */
    '6': { casa: 7, departamento: 13, oficina: 16, extrapolado: true }
  }
}

/**
 * LOSA DE CONCRETO REFORZADO — el dato que cambia el diseño en México, y que estaba
 * escondido en el texto, no en la tabla.
 *
 * `FUENTE:` mismo documento, párrafo posterior a la Tabla 3, literal: *«mediciones a
 * 5.2 GHz han mostrado que a incidencia normal la pérdida media adicional debida a una
 * losa típica de concreto reforzado con plafón falso es de 20 dB, con desviación estándar
 * de 1.5 dB. Las luminarias aumentaron la pérdida media a 30 dB (σ 3 dB), y los ductos de
 * aire bajo el piso la aumentaron a 36 dB (σ 5 dB).»* Y añade que **esos valores, y no
 * `Lf`, son los que hay que usar** en modelos específicos del sitio.
 *
 * ⚠️ **Por qué importa aquí y no es un detalle:** los 13 dB de la Tabla 3 corresponden a
 * un entrepiso residencial ligero. **Una losa de concreto reforzado —que es lo normal en
 * México— cuesta de 20 a 36 dB**, casi el triple. Diseñar una planta alta con 13 dB
 * subestima la pérdida y deja el piso de arriba sin señal.
 */
export const LOSA_CONCRETO = {
  fuente: 'UIT-R P.1238-7, mediciones a 5.2 GHz citadas tras la Tabla 3',
  simple: { db: 20, sigma: 1.5, que: 'Losa de concreto reforzado con plafón falso' },
  conLuminarias: { db: 30, sigma: 3, que: 'La misma losa con luminarias empotradas' },
  conDuctos: { db: 36, sigma: 5, que: 'Con ductos de aire bajo el piso' },
  porque: 'Los 13 dB de la Tabla 3 son de entrepiso ligero. Una losa de concreto reforzado ' +
          'cuesta de 20 a 36 dB: casi el triple. En construcción mexicana manda ésta.'
}

/**
 * DESVIACIÓN ESTÁNDAR DEL MODELO — Tabla 4, y es lo que convierte «estimado» en un número.
 *
 * `FUENTE:` UIT-R P.1238-7, Tabla 4, «Shadow fading statistics». Los valores son la
 * desviación estándar del desvanecimiento por sombra, en dB.
 *
 * **Esto es lo más honesto que se puede decir de una predicción de cobertura, y no lo
 * teníamos:** el modelo no da un número, da una campana. A 5.2 GHz en oficina la σ es de
 * **12 dB**, lo que significa que en aproximadamente uno de cada tres puntos medidos la
 * señal real se aparta más de 12 dB de lo predicho — en cualquiera de los dos sentidos.
 *
 * Doce decibeles son un factor de dieciséis en potencia. Por eso el módulo entero va
 * marcado `estimado` y por eso la documentación de Meraki insiste en el site survey: no es
 * cautela retórica, es la dispersión que publica la propia recomendación.
 */
export const DISPERSION = {
  fuente: 'UIT-R P.1238-7, Tabla 4',
  '2.4': { residencial: 8, oficina: 10, comercial: 10 },
  '5': { residencial: null, oficina: 12, comercial: null },
  porque: 'Es la desviación estándar del desvanecimiento por sombra. A 5 GHz en oficina son ' +
          '12 dB: en uno de cada tres puntos la señal real se aparta más de eso de lo ' +
          'predicho. Por eso esto se valida midiendo, no se promete.'
}

/**
 * QUÉ DICE LA RECOMENDACIÓN SOBRE CASOS QUE LA TABLA 2 NO CUBRE.
 *
 * `FUENTE:` mismo documento, conclusiones generales tras la Tabla 4. Son coeficientes `N`
 * observados en situaciones concretas, y explican discrepancias que de otro modo parecen
 * errores del modelo:
 *
 *   · **Trayectoria con línea de vista: N ≈ 20.** Domina la pérdida de espacio libre.
 *   · **Cuartos grandes y abiertos: N ≈ 20.** Casi todo el cuarto tiene línea de vista.
 *   · **Pasillos: N ≈ 18.** *Menos* que espacio libre — el pasillo actúa como guía de onda.
 *     Es contraintuitivo y es real: por un pasillo la señal llega más lejos de lo que
 *     predice la fórmula.
 *   · **Entre cuartos cerrados, atravesando muros: N ≈ 40.**
 *
 * ⚠️ **Ese último tiene consecuencia directa para el mercado de Soham Home.** Una casa
 * mexicana de muchos cuartos chicos con muros de block se parece más a N = 40 que al 30 de
 * la tabla. El módulo usa 30 porque es el valor tabulado para «departamento», pero **si el
 * levantamiento reporta muchos cuartos cerrados, 40 es el número honesto** y hay que
 * ofrecer poder subirlo.
 */
export const N_OBSERVADOS = {
  fuente: 'UIT-R P.1238-7, conclusiones generales',
  lineaDeVista: 20,
  cuartoAbierto: 20,
  pasillo: 18,
  entreCuartosCerrados: 40,
  porque: 'La recomendación observa N≈40 entre cuartos cerrados atravesando muros. Una casa ' +
          'mexicana de muchos cuartos chicos de block se parece más a eso que al 30 tabulado.'
}

/**
 * Pérdida total por atravesar `pisos` losas, en dB.
 *
 * `losa` elige entre la Tabla 3 —entrepiso ligero— y las mediciones de losa de concreto
 * reforzado que la propia recomendación dice usar en su lugar. En México manda la segunda,
 * así que es el valor por defecto: **usar la Tabla 3 en una casa de losa maciza subestima
 * la pérdida a menos de la mitad**, y deja la planta alta sin señal.
 */
export function perdidaPorPiso (pisos = 0, bandaId = '5', construccion = 'departamento', losa = 'concreto') {
  if (!pisos) return 0
  if (losa === 'ligero') {
    const tabla = ITU.Lf[bandaId] || ITU.Lf['5']
    return (tabla[construccion] ?? tabla.departamento) * pisos
  }
  const medido = LOSA_CONCRETO[losa] || LOSA_CONCRETO.simple
  return medido.db * pisos
}

export const LOSAS = [
  { id: 'simple', nombre: 'Losa de concreto', db: 20, que: 'Lo normal en construcción mexicana.' },
  { id: 'conLuminarias', nombre: 'Losa con luminarias empotradas', db: 30, que: 'Las luminarias abren huecos metálicos que reflejan.' },
  { id: 'conDuctos', nombre: 'Losa con ductos bajo el piso', db: 36, que: 'Lo peor: ducto metálico entre plantas.' },
  { id: 'ligero', nombre: 'Entrepiso ligero', db: null, que: 'Madera o panel. Usa el valor de la Tabla 3 de la ITU.' }
]

export const CONSTRUCCION = [
  { id: 'departamento', nombre: 'Muros de concreto o block', que: 'Lo normal en México, incluso en casa unifamiliar.' },
  { id: 'casa', nombre: 'Muros ligeros o tablaroca', que: 'Construcción ligera, interiores de madera o panel.' },
  { id: 'oficina', nombre: 'Oficina o local', que: 'Muchas divisiones, canceles y mobiliario metálico.' }
]

/**
 * Pérdida en interiores por ITU-R P.1238. `pisos` es cuántas losas hay de por medio.
 */
export function perdidaInterior (metros, mhz, { bandaId = '5', construccion = 'departamento', pisos = 0, losa = 'concreto' } = {}) {
  if (metros <= 1) return 0            // la recomendación acota su validez a d > 1 m
  const N = ITU.N[bandaId]?.[construccion] ?? ITU.N['5'].departamento
  const lf = perdidaPorPiso(pisos, bandaId, construccion, losa)
  return 20 * Math.log10(mhz) + N * Math.log10(metros) + lf - 28
}

export const BANDAS = [
  { id: '2.4', mhz: 2437, nombre: '2.4 GHz', que: 'Llega más lejos y atraviesa mejor. Más saturada y más lenta.' },
  { id: '5', mhz: 5500, nombre: '5 GHz', que: 'Mucho más rápida y limpia. Pierde 6.4 dB más a la misma distancia.' },
  { id: '6', mhz: 6500, nombre: '6 GHz', que: 'La más limpia porque casi nadie la usa todavía. La que menos alcanza.' }
]

export const bandaPorId = id => BANDAS.find(b => b.id === id) || BANDAS[1]

/**
 * Nivel recibido en un punto, en dBm.
 *
 *     Prx = EIRP − FSPL − Σ(atenuación de lo que haya en medio)
 *
 * `eirp` es la potencia radiada efectiva del punto de acceso, en dBm: sale de su ficha.
 * `obstaculos` es la lista de materiales que cruza la línea recta entre el punto de acceso
 * y el punto medido.
 */
export function nivelRecibido ({ eirp, metros, mhz, obstaculos = [] }) {
  const fspl = perdidaEspacioLibre(metros, mhz)
  const muros = obstaculos.reduce((s, id) => s + materialPorId(id).db, 0)
  return {
    dbm: eirp - fspl - muros,
    fspl, muros,
    estimado: true,
    porque: `${eirp} dBm de potencia menos ${fspl.toFixed(1)} dB de distancia` +
            (muros ? ` menos ${muros} dB de ${obstaculos.length} obstáculo(s)` : '')
  }
}

/**
 * Distancia a la que el nivel cae al borde de celda, en metros. **Modelo de interiores.**
 *
 * Se despeja `d` de la ecuación de la ITU, que es invertible en forma cerrada:
 *
 *     L = 20·log₁₀(f) + N·log₁₀(d) + L_f − 28
 *     d = 10^((EIRP − objetivo − 20·log₁₀(f) − L_f + 28) / N)
 *
 * NO recibe `obstaculos`: los muros interiores ya están dentro de `N`. Lo que sí recibe es
 * el **tipo de construcción**, que es como la ITU parametriza esa diferencia y —a
 * diferencia de contar muros uno por uno en un plano— es algo que el operador observa de
 * un vistazo al entrar a la casa.
 */
export function alcance ({ eirp, mhz, objetivo = BORDE_CELDA, bandaId = '5', construccion = 'departamento', pisos = 0, losa = 'concreto' }) {
  const N = ITU.N[bandaId]?.[construccion] ?? ITU.N['5'].departamento
  const lf = perdidaPorPiso(pisos, bandaId, construccion, losa)
  const exp = (eirp - objetivo - 20 * Math.log10(mhz) - lf + 28) / N
  return {
    metros: Math.pow(10, exp),
    N, lf, construccion, estimado: true,
    porque: `Con ${eirp} dBm y coeficiente N=${N} de la ITU para ${construccion}` +
            (lf ? ` más ${lf} dB por ${pisos} piso(s)` : '') +
            `, la señal cae a ${objetivo} dBm.`
  }
}

/* ============================================================================
   4. CATÁLOGO DE PUNTOS DE ACCESO
   ========================================================================== */

/**
 * `CONFIRMAR:` los valores de EIRP y de clientes recomendados **no salen de una ficha
 * leída en esta sesión.** Son órdenes de magnitud correctos para equipo de interior de
 * gama media, y se sustituyen por la ficha del modelo que Juan vaya a distribuir —Meraki
 * publica EIRP por banda y por modelo en el datasheet de cada MR.
 *
 * Se cargan marcados porque **sin ellos el módulo no puede estimar nada**, y estimar algo
 * declarado es más útil que una pantalla vacía. Pero gobiernan cuántos equipos se cotizan,
 * así que son de lo primero que hay que calibrar.
 *
 * `clientes` es el número recomendado por punto de acceso, no el máximo técnico: un equipo
 * admite cien asociaciones y da una experiencia mala mucho antes de llegar ahí.
 */
export const PUNTOS_ACCESO = [
  {
    id: 'ap-interior', nombre: 'Punto de acceso de interior', sinVerificar: true,
    eirp: { '2.4': 20, '5': 23, '6': 23 },
    clientes: 25, watts: 15, poe: '802.3af',
    para: 'Techo o muro dentro de la casa. Lo normal en recámaras y estancias.'
  },
  {
    id: 'ap-exterior', nombre: 'Punto de acceso de exterior', sinVerificar: true,
    eirp: { '2.4': 23, '5': 26, '6': 26 },
    clientes: 40, watts: 25, poe: '802.3at',
    para: 'Jardín, alberca, terraza. Va en gabinete resistente a intemperie.'
  }
]

export const apPorId = id => PUNTOS_ACCESO.find(a => a.id === id) || PUNTOS_ACCESO[0]

/* ============================================================================
   5. COBERTURA Y CANTIDAD
   ========================================================================== */

/**
 * Cobertura estimada de un conjunto de puntos de acceso sobre la planta.
 *
 * Reusa `cobertura` de `geometria.js` —la tercera vez, después del aspersor y la cámara—
 * tratando cada punto de acceso como un círculo de radio igual a su alcance. Es
 * deliberadamente el modelo simple: sin trazado de rayos ni obstáculos por punto, porque
 * un modelo elaborado sobre datos que nadie midió da falsa precisión, no precisión.
 *
 * `construccion` es el mando grueso que sí refleja la realidad y que el operador puede
 * observar de un vistazo: una casa de block con muchos cuartos cubre mucho menos que un
 * loft del mismo tamaño. Es la parametrización que usa la propia ITU, y es mejor que
 * contar muros en un plano porque no depende de que el plano esté bien.
 */
export function coberturaWifi (planta, aps, { bandaId = '5', construccion = 'departamento', pisos = 0, losa = 'concreto' } = {}) {
  const circulos = aps.map(a => {
    const ap = apPorId(a.modelo)
    return {
      x: a.x, y: a.y,
      radio: alcance({
        eirp: ap.eirp[bandaId], mhz: bandaPorId(bandaId).mhz, bandaId, construccion, pisos, losa
      }).metros,
      arco: 360
    }
  })
  return { ...cobertura(planta, circulos, 0.5), estimado: true }
}

/**
 * Cuántos puntos de acceso pedir, por los DOS criterios que importan, y cuál manda.
 *
 * Es el mismo patrón que el calibre del conductor —ampacidad contra caída de tensión— y
 * por la misma razón: **quien cotiza no puede saber cuál de los dos criterios domina**, y
 * equivocarse en cualquiera de los dos entrega una red que decepciona.
 *
 *   · **Por cobertura:** cuántos hacen falta para que la señal llegue a todos lados.
 *   · **Por capacidad:** cuántos hacen falta para los dispositivos que hay que atender.
 *
 * En una casa grande y vacía manda la cobertura. En un departamento chico con cuarenta
 * dispositivos manda la capacidad, y poner uno solo «porque alcanza» es el error típico.
 */
export function cuantosAP ({ m2, dispositivos, modelo = 'ap-interior', bandaId = '5', construccion = 'departamento', pisos = 0, losa = 'concreto' }) {
  const ap = apPorId(modelo)
  const r = alcance({
    eirp: ap.eirp[bandaId], mhz: bandaPorId(bandaId).mhz, bandaId, construccion, pisos, losa
  }).metros

  /* Área útil de un punto de acceso. Se usa un cuadrado inscrito en el círculo de alcance
     y no el círculo completo: las celdas se traslapan al embaldosar un plano, y contar el
     área del círculo entero da menos equipos de los que hacen falta. Lado = r·√2. */
  const areaUtil = Math.pow(r * Math.SQRT2, 2)
  const porCobertura = Math.max(1, Math.ceil(m2 / areaUtil))
  const porCapacidad = Math.max(1, Math.ceil(dispositivos / ap.clientes))

  const manda = porCobertura >= porCapacidad ? 'cobertura' : 'capacidad'
  return {
    cantidad: Math.max(porCobertura, porCapacidad),
    porCobertura, porCapacidad, manda,
    alcanceEstimado: Math.round(r * 10) / 10,
    areaUtil: Math.round(areaUtil),
    estimado: true,
    porque: manda === 'cobertura'
      ? `Con ${Math.round(r)} m de alcance estimado, cada punto cubre unos ${Math.round(areaUtil)} m², ` +
        `y son ${m2} m². Los ${dispositivos} dispositivos cabrían en ${porCapacidad}.`
      : `Los ${dispositivos} dispositivos piden ${porCapacidad} puntos a ${ap.clientes} cada uno. ` +
        `Por cobertura habrían bastado ${porCobertura}: aquí manda la cantidad de aparatos, no el tamaño.`
  }
}

/* ============================================================================
   6. LA CADENA COMPLETA
   ========================================================================== */

export function calcular ({
  planta, aps, rack, m2, dispositivos = 20, bandaId = '5', construccion = 'departamento', pisos = 0,
  losa = 'concreto',
  pisoRuido = -92, insumos = [], holguraObra = 1.10
}) {
  const cob = coberturaWifi(planta, aps, { bandaId, construccion, pisos, losa })
  const recomendacion = cuantosAP({ m2, dispositivos, bandaId, construccion, pisos, losa })

  /* Cableado: cada punto de acceso necesita su propio enlace al rack, igual que una
     cámara y por la misma razón. Se reusa la misma función. */
  const tramos = aps.map((a, i) => {
    const recto = Math.abs(a.x - rack.x) + Math.abs(a.y - rack.y)
    const metros = Math.ceil(recto * holguraObra)
    return { ap: i, metros, excedeLimite: metros > 100 }
  })
  const red = {
    tramos,
    metros: tramos.reduce((s, t) => s + t.metros, 0),
    fueraDeLimite: tramos.filter(t => t.excedeLimite).length
  }

  /* El SNR con el piso de ruido MEDIDO en la casa. Es el número que Meraki subraya como
     el que de verdad manda, y el único de todo el módulo que no es una estimación:
     el piso de ruido se lee del celular en la visita. */
  const snrEnBorde = BORDE_CELDA - pisoRuido
  const snr = {
    valor: snrEnBorde,
    pisoRuido,
    sirveDatos: snrEnBorde >= SNR.datos,
    sirveVoz: snrEnBorde >= SNR.voz,
    porque: snrEnBorde >= SNR.voz
      ? `Con piso de ruido de ${pisoRuido} dBm, el borde de celda da ${snrEnBorde} dB de SNR: ` +
        'alcanza para voz y video.'
      : snrEnBorde >= SNR.datos
        ? `Con piso de ruido de ${pisoRuido} dBm el SNR en el borde es ${snrEnBorde} dB: sirve para ` +
          `datos, pero no llega a los ${SNR.voz} dB que pide voz. Hay que acercar los puntos.`
        : `El piso de ruido de ${pisoRuido} dBm deja apenas ${snrEnBorde} dB de SNR. Eso NO es un ` +
          'problema de potencia: es interferencia, y subir la potencia la empeora.'
  }

  const potencia = aps.reduce((s, a) => s + apPorId(a.modelo).watts, 0)

  return {
    areaPlanta: area(planta),
    cobertura: cob,
    recomendacion,
    colocados: aps.length,
    faltan: Math.max(0, recomendacion.cantidad - aps.length),
    red, snr, potencia,
    banda: bandaPorId(bandaId),
    estimado: true,
    materiales: materiales({ aps, red, insumos })
  }
}

export function materiales ({ aps, red, insumos = [] }) {
  const lista = []
  const porModelo = {}
  for (const a of aps) porModelo[a.modelo] = (porModelo[a.modelo] || 0) + 1

  for (const [modelo, cantidad] of Object.entries(porModelo)) {
    const ap = apPorId(modelo)
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'ap', ap: modelo }),
      cantidad, unidad: 'pieza',
      de: `${cantidad} colocado(s) en el plano. ${ap.para}`
    }))
  }
  lista.push(renglon({
    insumo: resolver(insumos, { clase: 'utp' }),
    cantidad: red.metros, unidad: 'm',
    de: 'Un enlace por punto de acceso desde el rack, por trayectoria ortogonal, con holgura de obra'
  }))
  return lista
}
